import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, MapPin, Ruler, DollarSign, Navigation,
  AlertTriangle, FileText, ArrowRight, ArrowLeft,
  CheckCircle, Loader
} from 'lucide-react';
import { createDossier } from '../api/dossiers';

/* ─── DATA ─── */
const PAYS = [
  { flag:'🇹🇬', name:'Togo' },
  { flag:'🇧🇯', name:'Bénin' },
  { flag:'🇨🇮', name:"Côte d'Ivoire" },
  { flag:'🇸🇳', name:'Sénégal' },
  { flag:'🇲🇱', name:'Mali' },
  { flag:'🇧🇫', name:'Burkina Faso' },
  { flag:'🇳🇪', name:'Niger' },
  { flag:'🇬🇭', name:'Ghana' },
  { flag:'🇳🇬', name:'Nigeria' },
  { flag:'🇨🇲', name:'Cameroun' },
];
const DEVISES = ['FCFA', 'EUR', 'USD', 'GHS', 'NGN'];

/* ─── STEPS ─── */
const STEPS = [
  { id: 1, label: 'Vendeur',  icon: User,      desc: 'Qui vend ce terrain ?' },
  { id: 2, label: 'Terrain',  icon: MapPin,     desc: 'Localisation & caractéristiques' },
  { id: 3, label: 'Finances', icon: DollarSign, desc: 'Prix & devise' },
  { id: 4, label: 'Détails',  icon: FileText,   desc: 'Informations complémentaires' },
];

/* ─── PALETTE ─── */
const C = {
  sand:      '#f5ede0',
  sandDark:  '#e8d8c0',
  terra:     '#c2521a',
  terraLt:   '#e0784a',
  terraDk:   '#8a3510',
  forest:    '#1a3d28',
  forestMid: '#244d35',
  forestLt:  '#2f6645',
  gold:      '#c8902a',
  goldLt:    '#e8b84a',
  ink:       '#0e1a10',
  mist:      '#6e8070',
  cream:     '#faf6ef',
};

/* ─── CSS ─── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'DM Sans', sans-serif; }

/* ── PAGE ── */
.dc-page {
  min-height: 100vh;
  padding: 36px 40px 60px;
}
@media (max-width: 768px) { .dc-page { padding: 24px 16px 48px; } }

/* ── HEADER ── */
.dc-header {
  margin-bottom: 36px;
}
.dc-eyebrow {
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: ${C.terra};
  margin-bottom: 8px;
}
.dc-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -.035em;
  color: ${C.ink};
}
.dc-title em { font-style: italic; color: ${C.terra}; }
.dc-sub {
  margin-top: 6px;
  font-size: .88rem;
  color: ${C.mist};
}

/* ── STEPPER ── */
.stepper {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 40px;
  background: white;
  border-radius: 20px;
  padding: 6px;
  border: 1px solid rgba(26,61,40,.08);
  box-shadow: 0 4px 18px rgba(26,61,40,.06);
  overflow-x: auto;
}
.stepper::-webkit-scrollbar { height: 0; }
.step-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 18px;
  border-radius: 14px;
  cursor: pointer;
  transition: background .2s;
  flex: 1;
  min-width: 120px;
  position: relative;
  border: 1px solid transparent;
}
.step-item:hover:not(.active):not(.done) {
  background: rgba(26,61,40,.03);
}
.step-item.active {
  background: ${C.forest};
  border-color: ${C.forest};
}
.step-item.done {
  background: rgba(47,102,69,.08);
  border-color: rgba(47,102,69,.15);
  cursor: pointer;
}
.step-num {
  width: 30px; height: 30px;
  border-radius: 9px;
  display: grid; place-items: center;
  font-weight: 700;
  font-size: .8rem;
  flex: none;
  transition: background .2s, color .2s;
  background: rgba(26,61,40,.08);
  color: ${C.mist};
}
.step-item.active .step-num {
  background: rgba(245,237,224,.15);
  color: ${C.sand};
}
.step-item.done .step-num {
  background: ${C.forestLt};
  color: white;
}
.step-text { min-width: 0; }
.step-label {
  font-size: .84rem;
  font-weight: 600;
  color: ${C.mist};
  white-space: nowrap;
}
.step-item.active .step-label { color: ${C.sand}; }
.step-item.done .step-label { color: ${C.forest}; }
.step-desc {
  font-size: .72rem;
  color: rgba(110,128,112,.55);
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.step-item.active .step-desc { color: rgba(245,237,224,.5); }

/* progress bar */
.stepper-progress {
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.stepper-progress-track {
  flex: 1;
  height: 4px;
  border-radius: 999px;
  background: rgba(26,61,40,.07);
  overflow: hidden;
}
.stepper-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, ${C.forestLt}, ${C.terra});
  transition: width .5s cubic-bezier(.22,1,.36,1);
}
.stepper-progress-label {
  font-size: .76rem;
  font-weight: 700;
  color: ${C.mist};
  white-space: nowrap;
}

/* ── CONTENT CARD ── */
.form-card {
  background: white;
  border-radius: 24px;
  border: 1px solid rgba(26,61,40,.07);
  box-shadow: 0 8px 36px rgba(26,61,40,.07);
  overflow: hidden;
}
.form-card-header {
  padding: 28px 32px 24px;
  border-bottom: 1px solid rgba(26,61,40,.06);
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, rgba(26,61,40,.03), transparent);
}
.form-card-icon {
  width: 48px; height: 48px;
  border-radius: 14px;
  display: grid; place-items: center;
  background: ${C.forest};
  color: ${C.sand};
  flex: none;
  box-shadow: 0 8px 24px rgba(26,61,40,.18);
}
.form-card-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${C.ink};
  letter-spacing: -.025em;
}
.form-card-desc {
  font-size: .84rem;
  color: ${C.mist};
  margin-top: 2px;
}
.form-card-body {
  padding: 32px;
}
@media (max-width: 600px) {
  .form-card-header { padding: 20px; }
  .form-card-body   { padding: 20px; }
}

/* ── FIELDS ── */
.fields-grid {
  display: grid;
  gap: 20px;
}
.fields-row-2 { grid-template-columns: 1fr 1fr; }
.fields-row-3 { grid-template-columns: 1fr 1fr 1fr; }
@media (max-width: 700px) {
  .fields-row-2, .fields-row-3 { grid-template-columns: 1fr; }
}
.field-full { grid-column: 1 / -1; }

.field {
  display: grid;
  gap: 7px;
}
.field-label {
  font-size: .71rem;
  font-weight: 700;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: ${C.mist};
  display: flex;
  align-items: center;
  gap: 6px;
}
.field-label .required {
  color: ${C.terra};
  font-size: .65rem;
}
.field-input-wrap { position: relative; }
.field-icon {
  position: absolute;
  left: 14px;
  top: 50%; transform: translateY(-50%);
  color: rgba(110,128,112,.5);
  pointer-events: none;
}
.field-input {
  width: 100%;
  border: 1.5px solid rgba(26,61,40,.12);
  border-radius: 13px;
  background: ${C.cream};
  color: ${C.ink};
  padding: 13px 16px;
  font: 500 .95rem 'DM Sans', sans-serif;
  outline: none;
  transition: border-color .2s, box-shadow .2s, background .2s;
  appearance: none;
}
.field-input.with-icon { padding-left: 44px; }
.field-input:focus {
  border-color: ${C.terra};
  box-shadow: 0 0 0 4px rgba(194,82,26,.1);
  background: white;
}
.field-input:hover:not(:focus) {
  border-color: rgba(26,61,40,.22);
  background: white;
}
.field-input::placeholder { color: rgba(110,128,112,.4); }
.field-input.select-input {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236e8070' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  cursor: pointer;
}
textarea.field-input { resize: vertical; min-height: 110px; padding-top: 14px; }

/* ── PAYS SELECT ── */
.pays-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}
@media (max-width: 600px) { .pays-grid { grid-template-columns: repeat(3,1fr); } }
.pays-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 8px;
  border-radius: 12px;
  border: 1.5px solid rgba(26,61,40,.1);
  background: ${C.cream};
  cursor: pointer;
  transition: border-color .2s, background .2s, transform .2s;
  font: 500 .72rem 'DM Sans', sans-serif;
  color: ${C.mist};
}
.pays-btn:hover { border-color: rgba(26,61,40,.2); background: white; transform: translateY(-1px); }
.pays-btn.selected {
  border-color: ${C.forest};
  background: rgba(26,61,40,.07);
  color: ${C.forest};
  font-weight: 700;
}
.pays-btn-flag { font-size: 1.4rem; }

/* ── TOGGLE URGENCE ── */
.urgence-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-radius: 16px;
  border: 1.5px solid rgba(26,61,40,.1);
  background: ${C.cream};
  cursor: pointer;
  transition: border-color .25s, background .25s;
  gap: 16px;
}
.urgence-toggle.active {
  border-color: rgba(194,82,26,.35);
  background: rgba(194,82,26,.06);
}
.urgence-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.urgence-icon {
  width: 42px; height: 42px;
  border-radius: 12px;
  display: grid; place-items: center;
  background: rgba(194,82,26,.1);
  color: ${C.terra};
  flex: none;
  transition: background .25s;
}
.urgence-toggle.active .urgence-icon {
  background: rgba(194,82,26,.18);
}
.urgence-title {
  font-weight: 600;
  font-size: .92rem;
  color: ${C.ink};
}
.urgence-desc {
  font-size: .78rem;
  color: ${C.mist};
  margin-top: 2px;
}
.switch-track {
  width: 46px; height: 26px;
  border-radius: 999px;
  background: rgba(26,61,40,.12);
  position: relative;
  transition: background .25s;
  flex: none;
}
.urgence-toggle.active .switch-track {
  background: ${C.terra};
}
.switch-thumb {
  position: absolute;
  top: 3px; left: 3px;
  width: 20px; height: 20px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 2px 6px rgba(0,0,0,.15);
  transition: transform .25s cubic-bezier(.22,1,.36,1);
}
.urgence-toggle.active .switch-thumb {
  transform: translateX(20px);
}

/* ── ERROR ── */
.form-error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(194,82,26,.07);
  border: 1px solid rgba(194,82,26,.2);
  color: ${C.terraDk};
  border-radius: 14px;
  padding: 14px 18px;
  font-size: .88rem;
  line-height: 1.65;
  margin-bottom: 24px;
}
.form-error-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: ${C.terra};
  flex: none;
  margin-top: 5px;
}

/* ── RECAP ── */
.recap-grid {
  display: grid;
  gap: 14px;
}
.recap-section {
  background: ${C.cream};
  border-radius: 16px;
  padding: 18px 20px;
  border: 1px solid rgba(26,61,40,.07);
}
.recap-section-title {
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: ${C.terra};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.recap-rows { display: grid; gap: 9px; }
.recap-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: .88rem;
}
.recap-key { color: ${C.mist}; flex: none; }
.recap-val { font-weight: 600; color: ${C.ink}; text-align: right; }
.recap-urgent {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(194,82,26,.1);
  color: ${C.terraDk};
  font-size: .76rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
}

/* ── ACTIONS ── */
.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(26,61,40,.07);
}
.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 22px;
  border-radius: 13px;
  background: transparent;
  border: 1.5px solid rgba(26,61,40,.15);
  color: ${C.mist};
  font: 600 .9rem 'DM Sans', sans-serif;
  cursor: pointer;
  transition: border-color .2s, color .2s, background .2s;
  text-decoration: none;
}
.btn-back:hover { border-color: ${C.forest}; color: ${C.forest}; background: rgba(26,61,40,.03); }
.btn-next {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 13px 26px;
  border-radius: 13px;
  background: ${C.forest};
  color: ${C.sand};
  font: 600 .9rem 'DM Sans', sans-serif;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(26,61,40,.2);
  transition: transform .25s, box-shadow .25s, background .2s;
  position: relative;
  overflow: hidden;
}
.btn-next::before {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.07), transparent);
  transform: translateX(-100%);
  transition: transform .5s;
}
.btn-next:hover::before { transform: translateX(100%); }
.btn-next:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 16px 36px rgba(26,61,40,.28); }
.btn-next:disabled { opacity: .5; cursor: not-allowed; transform: none; }
.btn-next.submit { background: linear-gradient(135deg, ${C.forest}, ${C.forestMid}); }
.btn-steps-info {
  font-size: .78rem;
  color: rgba(110,128,112,.6);
}

/* ── SUCCESS ── */
.success-page {
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 24px;
  gap: 20px;
}
.success-ring {
  width: 80px; height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${C.forest}, ${C.forestLt});
  display: grid; place-items: center;
  box-shadow: 0 16px 40px rgba(26,61,40,.2);
  color: ${C.sand};
}
.success-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.2rem;
  font-weight: 700;
  color: ${C.ink};
  letter-spacing: -.03em;
}
.success-sub { font-size: .95rem; color: ${C.mist}; line-height: 1.7; max-width: 440px; }
`;

/* ─── STAGGER ─── 
const FADE_UP = {
  hidden:  { opacity: 0, y: 18 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: .5, delay: i * .06, ease: [.22, 1, .36, 1] } }),
};
*/
const SLIDE = {
  enter:  (dir) => ({ opacity: 0, x: dir > 0 ? 40 : -40 }),
  center: { opacity: 1, x: 0, transition: { duration: .45, ease: [.22, 1, .36, 1] } },
  exit:   (dir) => ({ opacity: 0, x: dir > 0 ? -40 : 40, transition: { duration: .3 } }),
};

/* ─── FIELD COMPONENT ─── */
function Field({ label, required, icon: Icon, children, className = '' }) {
  return (
    <div className={`field ${className}`}>
      <label className="field-label">
        {label}
        {required && <span className="required">*</span>}
      </label>
      <div className="field-input-wrap">
        {Icon && <Icon size={15} className="field-icon" />}
        {children}
      </div>
    </div>
  );
}

/* ─── MAIN ─── */
export default function DossierCreate() {
  const navigate = useNavigate();
  const [step, setStep]     = useState(1);
  const [dir, setDir]       = useState(1);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    nom_vendeur: '', contact_vendeur: '',
    localisation: '', pays: 'Togo', ville: '', quartier: '',
    superficie: '', prix_achat: '', devise: 'FCFA',
    coordonnees_gps: '', description: '', urgent: false,
  });

  const set = (name, value) => setForm(f => ({ ...f, [name]: value }));
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    set(name, type === 'checkbox' ? checked : value);
  };

  const goTo = (n) => { setDir(n > step ? 1 : -1); setStep(n); };
  const next = () => goTo(Math.min(step + 1, 4));
  const prev = () => goTo(Math.max(step - 1, 1));

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await createDossier(form);
      setSuccess(true);
      setTimeout(() => navigate(`/dossiers/${data.id}`), 2000);
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data, null, 2) : 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  const pct = Math.round((step / 4) * 100);
  const currentStep = STEPS[step - 1];
  const StepIcon = currentStep.icon;

  /* ── STEP CONTENT ── */
  const renderStep = () => {
    if (step === 1) return (
      <div className="fields-grid">
        <Field label="Nom du vendeur" required icon={User}>
          <input className="field-input with-icon" name="nom_vendeur" value={form.nom_vendeur}
            onChange={onChange} placeholder="Ex: Koffi Amadou" required />
        </Field>
        <Field label="Contact du vendeur" icon={User}>
          <input className="field-input with-icon" name="contact_vendeur" value={form.contact_vendeur}
            onChange={onChange} placeholder="Téléphone ou email" />
        </Field>
      </div>
    );

    if (step === 2) return (
      <div className="fields-grid" style={{ gap: 24 }}>
        <Field label="Localisation complète" required icon={MapPin} className="field-full">
          <input className="field-input with-icon" name="localisation" value={form.localisation}
            onChange={onChange} placeholder="Adresse complète du terrain" required />
        </Field>

        <div className="field">
          <label className="field-label">Pays <span className="required">*</span></label>
          <div className="pays-grid">
            {PAYS.map(({ flag, name }) => (
              <button key={name} type="button"
                className={`pays-btn ${form.pays === name ? 'selected' : ''}`}
                onClick={() => set('pays', name)}>
                <span className="pays-btn-flag">{flag}</span>
                <span>{name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="fields-grid fields-row-2">
          <Field label="Ville" required>
            <input className="field-input" name="ville" value={form.ville}
              onChange={onChange} placeholder="Ex: Lomé" required />
          </Field>
          <Field label="Quartier">
            <input className="field-input" name="quartier" value={form.quartier}
              onChange={onChange} placeholder="Ex: Tokoin" />
          </Field>
        </div>

        <Field label="Coordonnées GPS" icon={Navigation}>
          <input className="field-input with-icon" name="coordonnees_gps" value={form.coordonnees_gps}
            onChange={onChange} placeholder="Ex: 6.1374, 1.2123" />
        </Field>
      </div>
    );

    if (step === 3) return (
      <div className="fields-grid">
        <Field label="Superficie" required icon={Ruler}>
          <input className="field-input with-icon" type="number" name="superficie" value={form.superficie}
            onChange={onChange} placeholder="En m²" required />
        </Field>

        <div className="fields-grid fields-row-2">
          <Field label="Prix d'achat" icon={DollarSign}>
            <input className="field-input with-icon" type="number" name="prix_achat" value={form.prix_achat}
              onChange={onChange} placeholder="Montant" />
          </Field>
          <Field label="Devise">
            <select className="field-input select-input" name="devise" value={form.devise} onChange={onChange}>
              {DEVISES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
        </div>

        <div
          className={`urgence-toggle ${form.urgent ? 'active' : ''}`}
          onClick={() => set('urgent', !form.urgent)}>
          <div className="urgence-left">
            <div className="urgence-icon">
              <AlertTriangle size={20} />
            </div>
            <div>
              <div className="urgence-title">Marquer comme urgent</div>
              <div className="urgence-desc">Traitement prioritaire sous 24h</div>
            </div>
          </div>
          <div className="switch-track">
            <div className="switch-thumb" />
          </div>
        </div>
      </div>
    );

    if (step === 4) return (
      <div className="fields-grid">
        <Field label="Description / Remarques" icon={FileText} className="field-full">
          <textarea className="field-input with-icon" name="description" value={form.description}
            onChange={onChange} placeholder="Informations supplémentaires, contexte de l'achat, documents disponibles…" />
        </Field>

        {/* recap */}
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: C.mist, marginBottom: 14 }}>
            Récapitulatif du dossier
          </div>
          <div className="recap-grid">
            <div className="recap-section">
              <div className="recap-section-title"><User size={12} /> Vendeur</div>
              <div className="recap-rows">
                <div className="recap-row"><span className="recap-key">Nom</span><span className="recap-val">{form.nom_vendeur || '—'}</span></div>
                <div className="recap-row"><span className="recap-key">Contact</span><span className="recap-val">{form.contact_vendeur || '—'}</span></div>
              </div>
            </div>
            <div className="recap-section">
              <div className="recap-section-title"><MapPin size={12} /> Terrain</div>
              <div className="recap-rows">
                <div className="recap-row"><span className="recap-key">Localisation</span><span className="recap-val">{form.localisation || '—'}</span></div>
                <div className="recap-row"><span className="recap-key">Pays</span><span className="recap-val">{form.pays}</span></div>
                <div className="recap-row"><span className="recap-key">Ville</span><span className="recap-val">{form.ville || '—'}</span></div>
                {form.quartier && <div className="recap-row"><span className="recap-key">Quartier</span><span className="recap-val">{form.quartier}</span></div>}
              </div>
            </div>
            <div className="recap-section">
              <div className="recap-section-title"><DollarSign size={12} /> Finances</div>
              <div className="recap-rows">
                <div className="recap-row"><span className="recap-key">Superficie</span><span className="recap-val">{form.superficie ? `${form.superficie} m²` : '—'}</span></div>
                <div className="recap-row"><span className="recap-key">Prix</span><span className="recap-val">{form.prix_achat ? `${parseInt(form.prix_achat).toLocaleString('fr')} ${form.devise}` : '—'}</span></div>
                <div className="recap-row">
                  <span className="recap-key">Priorité</span>
                  <span className="recap-val">
                    {form.urgent
                      ? <span className="recap-urgent"><AlertTriangle size={11} /> Urgent</span>
                      : 'Standard'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (success) return (
    <div className="dc-page">
      <style>{css}</style>
      <motion.div className="success-page"
        initial={{ opacity: 0, scale: .92 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: .6, ease: [.22, 1, .36, 1] }}>
        <motion.div className="success-ring"
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ delay: .1, duration: .5, ease: [.22, 1, .36, 1] }}>
          <CheckCircle size={36} />
        </motion.div>
        <div className="success-title">Dossier soumis !</div>
        <p className="success-sub">
          Votre dossier a été créé avec succès. Notre équipe va commencer la vérification dans les plus brefs délais. Redirection en cours…
        </p>
        <div style={{ display:'flex', alignItems:'center', gap:8, color: C.mist, fontSize:'.82rem' }}>
          <div style={{ width:14, height:14, border:'2px solid rgba(110,128,112,.3)', borderTopColor: C.terra, borderRadius:'50%', animation:'spin .8s linear infinite' }} />
          Redirection vers votre dossier…
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="dc-page">
      <style>{css}</style>

      {/* ── HEADER ── */}
      <motion.div className="dc-header" initial={{ opacity:0, y:-14 }} animate={{ opacity:1, y:0 }} transition={{ duration:.5, ease:[.22,1,.36,1] }}>
        <div className="dc-eyebrow">Nouveau dossier</div>
        <h1 className="dc-title">Soumettre une <em>vérification</em></h1>
        <p className="dc-sub">Renseignez les informations du terrain pour lancer votre enquête.</p>
      </motion.div>

      {/* ── STEPPER ── */}
      <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay:.1, duration:.5, ease:[.22,1,.36,1] }}>
        <div className="stepper">
          {STEPS.map((s) => {
            const Icon = s.icon;
            const done = s.id < step;
            const active = s.id === step;
            return (
              <div key={s.id} className={`step-item ${active ? 'active' : ''} ${done ? 'done' : ''}`}
                onClick={() => done && goTo(s.id)}>
                <div className="step-num">
                  {done ? <CheckCircle size={14} /> : active ? <Icon size={14} /> : s.id}
                </div>
                <div className="step-text">
                  <div className="step-label">{s.label}</div>
                  <div className="step-desc">{s.desc}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="stepper-progress">
          <div className="stepper-progress-track">
            <div className="stepper-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="stepper-progress-label">Étape {step}/4 · {pct}%</div>
        </div>
      </motion.div>

      {/* ── FORM CARD ── */}
      <motion.div className="form-card" initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:.18, duration:.55, ease:[.22,1,.36,1] }}>
        <div className="form-card-header">
          <div className="form-card-icon">
            <StepIcon size={22} />
          </div>
          <div>
            <div className="form-card-title">{currentStep.label}</div>
            <div className="form-card-desc">{currentStep.desc}</div>
          </div>
        </div>

        <div className="form-card-body">
          {/* error */}
          <AnimatePresence>
            {error && (
              <motion.div className="form-error"
                initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
                exit={{ opacity:0, height:0 }}>
                <div className="form-error-dot" />
                <pre style={{ fontFamily:'inherit', whiteSpace:'pre-wrap', fontSize:'.83rem' }}>{error}</pre>
              </motion.div>
            )}
          </AnimatePresence>

          {/* step content */}
          <AnimatePresence custom={dir} mode="wait">
            <motion.div key={step} custom={dir}
              variants={SLIDE} initial="enter" animate="center" exit="exit">
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {/* actions */}
          <div className="form-actions">
            <div>
              {step > 1
                ? <button className="btn-back" onClick={prev}><ArrowLeft size={15} /> Précédent</button>
                : <button className="btn-back" onClick={() => navigate('/dossiers')}><ArrowLeft size={15} /> Annuler</button>
              }
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <span className="btn-steps-info">{step} / 4</span>
              {step < 4
                ? <button className="btn-next" onClick={next}>
                    Suivant <ArrowRight size={15} />
                  </button>
                : <button className="btn-next submit" onClick={handleSubmit} disabled={loading}>
                    {loading
                      ? <><Loader size={15} style={{ animation:'spin .8s linear infinite' }} /> Envoi…</>
                      : <>Soumettre le dossier <ArrowRight size={15} /></>
                    }
                  </button>
              }
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}